import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, CheckCircle2, AlertTriangle, Trash2, Send, Inbox } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  fetchMyEmailSettings,
  saveMyEmailSettings,
  deleteMyEmailSettings,
  sendMyEmailProbe,
  type UserEmailSettingsSummary,
} from '@/lib/myEmailSettings';

const HOSTS_PRESET = [
  { label: 'Zoho Mail (smtp.zoho.com)', host: 'smtp.zoho.com', port: 587 },
  { label: 'Zoho Mail EU', host: 'smtp.zoho.eu', port: 587 },
  { label: 'Gmail / Google Workspace', host: 'smtp.gmail.com', port: 587 },
  { label: 'Outlook 365', host: 'smtp.office365.com', port: 587 },
];

export function MyEmailSettingsPage() {
  const { t } = useTranslation();

  const [current, setCurrent] = useState<UserEmailSettingsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [host, setHost] = useState('smtp.zoho.com');
  const [port, setPort] = useState(587);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [fromName, setFromName] = useState('');
  const [starttls, setStarttls] = useState(true);

  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [testRecipient, setTestRecipient] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetchMyEmailSettings()
      .then((s) => {
        if (cancelled) return;
        setCurrent(s);
        if (s) {
          setHost(s.host);
          setPort(s.port);
          setUsername(s.username);
          setFromName(s.fromName ?? '');
          setStarttls(s.starttls);
        }
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function onSave(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSuccess(null);
    if (!password) {
      setError(t('emailSettings.errors.password_required'));
      return;
    }
    setSaving(true);
    try {
      const updated = await saveMyEmailSettings({
        host,
        port,
        username,
        password,
        fromName: fromName || null,
        starttls,
      });
      setCurrent(updated);
      setPassword('');
      setSuccess(t('emailSettings.saved'));
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message?: unknown }).message)
          : t('emailSettings.errors.save_failed');
      setError(msg);
    } finally {
      setSaving(false);
    }
  }

  async function onSendTest() {
    setError(null);
    setSuccess(null);
    setTesting(true);
    try {
      const res = await sendMyEmailProbe(testRecipient || undefined);
      if (res.ok) {
        setSuccess(res.message ?? t('emailSettings.probe_ok'));
      } else {
        setError(res.detail ?? res.error ?? t('emailSettings.errors.probe_failed'));
      }
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message?: unknown }).message)
          : t('emailSettings.errors.probe_failed');
      setError(msg);
    } finally {
      setTesting(false);
    }
  }

  async function onDisconnect() {
    if (!confirm(t('emailSettings.disconnect_confirm'))) return;
    setError(null);
    setSuccess(null);
    try {
      await deleteMyEmailSettings();
      setCurrent(null);
      setPassword('');
      setSuccess(t('emailSettings.disconnected'));
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message?: unknown }).message)
          : t('emailSettings.errors.disconnect_failed');
      setError(msg);
    }
  }

  if (loading) {
    return <div className="p-6 text-sm text-muted-foreground">{t('common.loading')}</div>;
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-6">
      <header>
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <Mail className="h-6 w-6" /> {t('emailSettings.title')}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">{t('emailSettings.subtitle')}</p>
        <Link
          to="/account/email-incoming"
          className="mt-2 inline-flex items-center gap-1 text-sm text-primary hover:underline"
          data-testid="link-incoming-email"
        >
          <Inbox className="h-4 w-4" /> {t('emailSettings.incomingLink')}
        </Link>
      </header>

      {current && (
        <div className="rounded-lg border bg-card p-4">
          <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold">
            <CheckCircle2 className="h-4 w-4 text-green-600" /> {t('emailSettings.connected_as')}
          </h3>
          <div className="text-sm">
            <div>
              <span className="text-muted-foreground">{t('emailSettings.fields.username')}: </span>
              <span className="font-medium">{current.username}</span>
            </div>
            <div>
              <span className="text-muted-foreground">{t('emailSettings.fields.host')}: </span>
              <span className="font-medium">
                {current.host}:{current.port}
                {current.starttls ? ' (STARTTLS)' : ''}
              </span>
            </div>
            {current.fromName && (
              <div>
                <span className="text-muted-foreground">{t('emailSettings.fields.from_name')}: </span>
                <span className="font-medium">{current.fromName}</span>
              </div>
            )}
            {current.lastProbeAt && (
              <div className="mt-2 text-xs text-muted-foreground">
                {t('emailSettings.last_probe')}: {current.lastProbeStatus ?? '—'}
                {current.lastProbeError && (
                  <span className="text-amber-300"> — {current.lastProbeError}</span>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <form onSubmit={onSave} className="space-y-4 rounded-lg border bg-card p-4">
        <h3 className="text-sm font-semibold">
          {current ? t('emailSettings.update_form_title') : t('emailSettings.connect_form_title')}
        </h3>

        <div className="space-y-1">
          <label className="text-sm font-medium">{t('emailSettings.fields.host')}</label>
          <Input value={host} onChange={(e) => setHost(e.target.value)} placeholder="smtp.zoho.com" required />
          <div className="flex flex-wrap gap-2 pt-1">
            {HOSTS_PRESET.map((p) => (
              <button
                key={p.host}
                type="button"
                className="rounded-md border px-2 py-1 text-xs hover:bg-accent"
                onClick={() => {
                  setHost(p.host);
                  setPort(p.port);
                }}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-sm font-medium">{t('emailSettings.fields.port')}</label>
            <Input
              type="number"
              value={port}
              onChange={(e) => setPort(Number(e.target.value))}
              placeholder="587"
              required
            />
          </div>
          <div className="space-y-1 self-end">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={starttls}
                onChange={(e) => setStarttls(e.target.checked)}
              />
              {t('emailSettings.fields.starttls')}
            </label>
          </div>
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium">
            {t('emailSettings.fields.username')} <span className="text-red-500">*</span>
          </label>
          <Input
            type="email"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="you@yourdomain.com"
            required
          />
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium">
            {t('emailSettings.fields.password')} <span className="text-red-500">*</span>
          </label>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="16-char app password"
            required
          />
          <p className="text-xs text-muted-foreground">{t('emailSettings.app_password_hint')}</p>
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium">{t('emailSettings.fields.from_name')}</label>
          <Input value={fromName} onChange={(e) => setFromName(e.target.value)} placeholder="Your Name" />
        </div>

        {error && (
          <div className="rounded-md border border-red-800 bg-red-950/40 px-3 py-2 text-sm text-red-300">
            {error}
          </div>
        )}
        {success && (
          <div className="rounded-md border border-green-800 bg-green-950/40 px-3 py-2 text-sm text-green-300">
            {success}
          </div>
        )}

        <div className="flex gap-2">
          <Button type="submit" disabled={saving}>
            {saving ? t('common.loading') : current ? t('emailSettings.update') : t('emailSettings.connect')}
          </Button>
          {current && (
            <Button type="button" variant="ghost" onClick={onDisconnect}>
              <Trash2 className="mr-1 h-4 w-4" /> {t('emailSettings.disconnect')}
            </Button>
          )}
        </div>
      </form>

      {current && (
        <div className="space-y-2 rounded-lg border bg-card p-4">
          <h3 className="text-sm font-semibold">{t('emailSettings.test.title')}</h3>
          <p className="text-xs text-muted-foreground">{t('emailSettings.test.subtitle')}</p>
          <div className="flex gap-2">
            <Input
              value={testRecipient}
              onChange={(e) => setTestRecipient(e.target.value)}
              placeholder={current.username}
            />
            <Button type="button" onClick={onSendTest} disabled={testing}>
              {testing ? (
                <span className="text-sm">{t('emailSettings.test.sending')}</span>
              ) : (
                <>
                  <Send className="mr-1 h-4 w-4" /> {t('emailSettings.test.send')}
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      <div className="rounded-lg border border-amber-800 bg-amber-950/40 p-4 text-xs text-amber-200">
        <div className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4" />
          <div>
            <strong>{t('emailSettings.gotcha.title')}</strong>
            <p className="mt-1">{t('emailSettings.gotcha.body')}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
